import type { YYYYMMDateString, YYYYMMDDDateString } from "@s2z-platform/types";
import { addMonths, endOfMonth } from "date-fns";
import { convertToYYYYMM } from "./utils";
import type { EstimateStatusEnum } from "@s2z-platform/constants";

// All of these are used by front and back, so designed for DB entity and the santised API response version
// eg. EstimateEntity and ApiEstimate

type CommonEstimateEntityAndApiResAttributes = {
  effectiveDate: string;
  status: EstimateStatusEnum;
};

export function sortEstimatesEarliestFirst<
  T extends CommonEstimateEntityAndApiResAttributes,
>(a: T, b: T) {
  return (
    new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime()
  );
}

/** get estimate with effective date on same as entry or soonest before  */
export function genericGetLastEstimateBeforeDate<
  T extends CommonEstimateEntityAndApiResAttributes,
>(estimates: T[], date: Date): T | undefined {
  const targetYear = date.getFullYear();
  const targetMonth = date.getMonth();

  const yearMonthMatch = estimates.find((e) => {
    const estimateDate = new Date(e.effectiveDate);
    return (
      estimateDate.getFullYear() === targetYear &&
      estimateDate.getMonth() === targetMonth
    );
  });

  if (yearMonthMatch) return yearMonthMatch;

  const validEstimates = estimates.filter(
    (e) => new Date(e.effectiveDate) <= date,
  );

  if (!validEstimates.length) return undefined;

  return validEstimates.sort(sortEstimatesEarliestFirst)[
    validEstimates.length - 1
  ];
}

/**
 * Filters estimates to return those relevant for target projection.
 * Returns an array of estimates of particular status, sorted by date.
 * The first item in the array is the base year footprint, earlier estimates are removed.
 * - If a verified/declared estimate exists in the base fiscal year → return only verified/declared estimates.
 * - Else if there's an estimated one in that fiscal year → return all types of estimates.
 * - Else → return undefined (not usable).
 */
export function genericProcessEstimatesForTarget<
  E extends CommonEstimateEntityAndApiResAttributes,
  T extends { baseYearDate: YYYYMMDDDateString | YYYYMMDateString },
>(estimates: E[] | undefined, target: T | undefined): E[] | undefined {
  if (!estimates?.length || !target) return undefined;

  const sorted = [...estimates].sort(sortEstimatesEarliestFirst);
  const verifiedOrDeclared = sorted.filter((e) => e.status !== "estimated");
  const estimatedOnly = sorted.filter((e) => e.status === "estimated");

  const baseDate = new Date(target.baseYearDate);
  const endOfFiscalBaseYear = endOfMonth(addMonths(baseDate, 11));

  const baseYearVerifiedOrDeclared = genericGetLastEstimateBeforeDate(
    verifiedOrDeclared,
    endOfFiscalBaseYear,
  );

  if (baseYearVerifiedOrDeclared) {
    return verifiedOrDeclared.filter(
      (e) =>
        new Date(e.effectiveDate).getTime() >=
        new Date(baseYearVerifiedOrDeclared.effectiveDate).getTime(),
    );
  }

  const baseYearEstimated = genericGetLastEstimateBeforeDate(
    estimatedOnly,
    endOfFiscalBaseYear,
  );

  if (baseYearEstimated) {
    return sorted.filter(
      (e) =>
        new Date(e.effectiveDate).getTime() >=
        new Date(baseYearEstimated.effectiveDate).getTime(),
    );
  }

  return undefined;
}

function getFiscalYear(
  effectiveDate: Date,
  fiscalYearStartMonth: number,
): number {
  const month = effectiveDate.getMonth() + 1; // getMonth() returns 0-11
  const year = effectiveDate.getFullYear();
  return month >= fiscalYearStartMonth ? year + 1 : year;
}

/**
 * Returns an object with fiscal year as keys and the relevant footprint for each fiscal year.
 * - Creates entries for as many years between the fiscalYearStartDate and the fiscal year of latest dated estimate
 * - If no footprint exists for a year, the value will be `undefined`.
 * @param fiscalYearStartDate - The start date of the base year (e.g., "2024-04").
 * @param estimates - A list of footprints (EstimateEntity or ApiEstimate) to select from .
 * @returns An object with fiscal years as keys and footprints as values.
 */
export function genericGetEstimatesByFiscalYear<
  T extends CommonEstimateEntityAndApiResAttributes,
>(fiscalYearStartDate: YYYYMMDateString, estimates: T[]) {
  const result: Record<YYYYMMDateString, T | undefined> = {};

  if (!estimates.length) return result;

  const baseDate = new Date(fiscalYearStartDate);
  const baseYear = baseDate.getFullYear();
  const baseMonth = baseDate.getMonth(); // 0-indexed

  const sorted = [...estimates].sort(sortEstimatesEarliestFirst);

  const lastFiscalYear = getFiscalYear(
    new Date(sorted[sorted.length - 1].effectiveDate),
    baseMonth,
  );

  for (let year = baseYear; year <= lastFiscalYear; year++) {
    const fiscalYearEnd = endOfMonth(addMonths(new Date(year, baseMonth), 11));
    const match = genericGetLastEstimateBeforeDate(sorted, fiscalYearEnd);
    const key = convertToYYYYMM(new Date(year, baseMonth, 1));

    result[key] = match || undefined;
    // Remove the matched estimate from sorted array if it exists
    if (match) {
      const indexToRemove = sorted.findIndex(
        (e) => e.effectiveDate === match.effectiveDate,
      );
      if (indexToRemove !== -1) {
        sorted.splice(indexToRemove, 1);
      }

      // Stop if we've run out of estimates or no match found in this year
      if (!sorted.length || !match) break;
    }
  }

  return result;
}
